import { Navigate, useNavigate } from '@tanstack/react-router';

import chooseSuppliesIcon from '@/assets/product-pages/selected-product-primer/choose-supplies.svg';
import confirmInsuranceIcon from '@/assets/product-pages/selected-product-primer/confirm-insurance.svg';
import scheduleProviderCallIcon from '@/assets/product-pages/selected-product-primer/schedule-provider-call.svg';
import { Heading1 } from '@/components/common/Heading1';
import { StickyFooterCta } from '@/components/common/StickyFooterCta';
import { Card, CardContent } from '@/components/ui/card';
import { useReferrerProduct } from '@/hooks/use-referrer-product';
import { hasProductStep } from '@/lib/products';

const nextSteps = [
  {
    icon: confirmInsuranceIcon,
    label: '1: Confirm insurance',
  },
  {
    icon: chooseSuppliesIcon,
    label: '2: Choose supplies',
  },
  {
    icon: scheduleProviderCallIcon,
    label: '3: Schedule provider call',
  },
];

export function SelectedProductPrimer() {
  const navigate = useNavigate();
  const { product, isLoading, isNotFound } = useReferrerProduct();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status">
        <p className="text-charcoal-500 italic">Loading your product...</p>
      </div>
    );
  }

  if (isNotFound || !product || !hasProductStep(product.category.name)) {
    return <Navigate to="/0" replace />;
  }

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-3 pb-28">
      <Card className="mx-auto w-full items-center gap-0 rounded-xl border-neutral-300 bg-white p-4 text-center shadow-card">
        <CardContent className="flex w-full flex-col items-center gap-2">
          <div className="rounded bg-blue-50 px-1.5 py-1 text-sm leading-none font-medium text-blue-400">
            $0 with insurance
          </div>

          <Heading1 className="mb-0 w-full max-w-2xl text-center text-[32px] leading-[1.15] sm:mb-0 sm:text-[32px]">
            You’re minutes away from ordering your {product.name}
          </Heading1>

          <p className="-mt-1 w-full max-w-xl text-center text-base leading-[1.4] text-neutral-600 sm:text-lg">
            You can order more products in the next steps.
          </p>

          {product.image && (
            <img
              src={product.image}
              alt={product.name}
              className="animate-product-image-hover size-50 object-contain"
            />
          )}
        </CardContent>
      </Card>

      <Card className="mx-auto w-full gap-0 rounded-lg border-neutral-300 bg-white p-5 shadow-card">
        <CardContent className="w-full">
          <h2 className="mb-5 text-center font-martina text-2xl leading-[1.15]">Next steps</h2>
          <ol className="divide-y divide-neutral-300">
            {nextSteps.map((step) => (
              <li
                key={step.label}
                className="flex min-h-[80px] items-center py-4 first:pt-0 last:pb-0 sm:min-h-17 sm:justify-center sm:py-2.5"
              >
                <span className="flex w-full items-center gap-4 sm:w-80">
                  <img src={step.icon} alt="" className="size-12 shrink-0" aria-hidden="true" />
                  <span className="text-[17px] leading-[1.4] sm:text-lg">{step.label}</span>
                </span>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <StickyFooterCta
        onClick={() => navigate({ to: '/0' })}
        contentClassName="max-w-sm"
        className="h-12 text-base"
      >
        Continue
      </StickyFooterCta>
    </section>
  );
}
